import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Cookies from "js-cookie";
import axios from "axios";
import API_ENDPOINTS from "../config/api";
import { formatBuildingLabel } from "../utils/apartmentDisplay";

const { KEYS: COOKIE_KEYS } = API_ENDPOINTS.COOKIE_CONFIG;

const InquiryButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    // 쿠키에서 user_info 가져오기
    const fetchUserData = async () => {
      const userInfoFromCookie = Cookies.get(COOKIE_KEYS.USER_INFO);

      if (userInfoFromCookie) {
        try {
          const parsedUserInfo = JSON.parse(userInfoFromCookie);
          setUserData(parsedUserInfo);
        } catch (error) {}
      } else {
        // 쿠키에 user_info가 없으면 API에서 가져오기
        try {
          const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
          if (accessToken) {
            const response = await axios({
              method: "GET",
              url: `${API_ENDPOINTS.BASE_URL}/mobile/user`,
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });

            if (response.data) {
              setUserData(response.data);
              // 쿠키에 저장
              Cookies.set(
                COOKIE_KEYS.USER_INFO,
                JSON.stringify(response.data),
                {
                  expires: 7, // 7일 동안 유효
                  ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
                }
              );
            }
          }
        } catch (error) {}
      }
    };

    // 첫 로드와 모달이 열릴 때마다 최신 사용자 정보를 가져옴
    fetchUserData();
  }, [isModalOpen]);

  const openModal = () => {
    setIsModalOpen(true);
    // 스크롤 방지
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    // 스크롤 다시 허용
    document.body.style.overflow = "";

    // 일정 시간 후 성공 메시지 초기화
    if (submitSuccess) {
      setTimeout(() => {
        setSubmitSuccess(false);
        setFormData({ title: "", content: "" });
      }, 300);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.content) {
      alert("제목과 내용을 모두 입력해주세요.");
      return;
    }

    setIsSubmitting(true);

    try {
      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
      if (!accessToken) {
        alert("로그인이 필요합니다.");
        return;
      }

      // 문의 API 호출
      await axios({
        method: "POST",
        url: `${API_ENDPOINTS.BASE_URL}/mobile/inquiry`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        data: {
          inquiryTitle: formData.title,
          inquiryContent: formData.content,
        },
      });

      setSubmitSuccess(true);

      // 성공 후 3초 뒤 모달 닫기
      setTimeout(() => {
        closeModal();
      }, 2000);
    } catch (error) {
      alert("문의 제출 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 사용자 정보 포맷
  const formatUserName = () => {
    if (!userData) {
      // Access Token이 있는지 확인
      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
      if (!accessToken) {
        return "로그인 필요";
      }
      return "사용자";
    }

    return userData.residentName || "사용자";
  };

  const formatUserInfo = () => {
    if (!userData) {
      // Access Token이 있는지 확인
      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
      if (!accessToken) {
        return "로그인 후 이용 가능합니다";
      }
      return "정보를 불러오는 중...";
    }

    const apartment = userData.apartmentName || "";
    const block = userData.apartmentBlock || "";
    const unit = userData.apartmentUnit || "";

    if (!apartment && !block && !unit) {
      return "정보가 없습니다";
    }

    return `${apartment} ${formatBuildingLabel(
      block,
      Cookies.get("apartmentCode"),
    )} ${unit}호`;
  };

  // 버튼 애니메이션
  const buttonVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: { type: "spring", stiffness: 260, damping: 20 },
    },
    hover: { scale: 1.1, transition: { duration: 0.2 } },
    tap: { scale: 0.95 },
  };

  // 모달 애니메이션
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 20,
      transition: {
        duration: 0.2,
      },
    },
  };
  return (
    <>
      <motion.button
        className="inquiry-button"
        variants={buttonVariants}
        initial="initial"
        animate="animate"
        whileHover="hover"
        whileTap="tap"
        onClick={openModal}
        style={{
          position: "fixed",
          bottom: "24px",
          right: "24px",
          width: "60px",
          height: "60px",
          borderRadius: "50%",
          backgroundColor: "#7A4FFF",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "none",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          zIndex: 999,
        }}
      >
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
            fill="white"
          />
        </svg>
      </motion.button>

      <AnimatePresence>
        {isModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
            onClick={closeModal}
          >
            <motion.div
              className="inquiry-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "90%",
                maxWidth: "500px",
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <div style={{ marginBottom: "24px" }}>
                <h2
                  style={{
                    fontSize: "22px",
                    fontWeight: "bold",
                    marginBottom: "8px",
                    textAlign: "center",
                  }}
                >
                  온라인 문의
                </h2>
                <p
                  style={{
                    color: "#666",
                    fontSize: "14px",
                    textAlign: "center",
                  }}
                >
                  궁금한 점이나 요청사항을 작성해주세요.
                </p>
              </div>

              {submitSuccess ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 0",
                  }}
                >
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="12" cy="12" r="10" fill="#4CAF50" />
                    <path
                      d="M9 12L11 14L15 10"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <h3
                    style={{
                      fontSize: "18px",
                      marginTop: "16px",
                      marginBottom: "8px",
                      color: "#4CAF50",
                    }}
                  >
                    문의가 접수되었습니다
                  </h3>
                  <p style={{ color: "#666", fontSize: "14px" }}>
                    빠른 시일 내에 답변 드리겠습니다.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      htmlFor="userName"
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        color: "#555",
                      }}
                    >
                      이름
                    </label>
                    <div
                      style={{
                        padding: "12px",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        backgroundColor: "#F7F7F7",
                        fontSize: "15px",
                      }}
                    >
                      {formatUserName()}
                    </div>
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label
                      htmlFor="userInfo"
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        color: "#555",
                      }}
                    >
                      정보
                    </label>
                    <div
                      style={{
                        padding: "12px",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        backgroundColor: "#F7F7F7",
                        fontSize: "15px",
                      }}
                    >
                      {formatUserInfo()}
                    </div>
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label
                      htmlFor="title"
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        color: "#555",
                      }}
                    >
                      제목 <span style={{ color: "#FF4D4F" }}>*</span>
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="제목을 입력해주세요"
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        fontSize: "15px",
                      }}
                      required
                    />
                  </div>

                  <div style={{ marginBottom: "24px" }}>
                    <label
                      htmlFor="content"
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        color: "#555",
                      }}
                    >
                      문의내용 <span style={{ color: "#FF4D4F" }}>*</span>
                    </label>
                    <textarea
                      id="content"
                      name="content"
                      value={formData.content}
                      onChange={handleInputChange}
                      placeholder="문의하실 내용을 상세히 작성해주세요"
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        minHeight: "120px",
                        fontSize: "15px",
                        resize: "vertical",
                      }}
                      required
                    />
                  </div>

                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      type="button"
                      onClick={closeModal}
                      style={{
                        flex: 1,
                        padding: "14px",
                        backgroundColor: "#F1F1F1",
                        color: "#555",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "15px",
                        fontWeight: "500",
                        cursor: "pointer",
                      }}
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      style={{
                        flex: 2,
                        padding: "14px",
                        backgroundColor: "#7A4FFF",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "15px",
                        fontWeight: "500",
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                        opacity: isSubmitting ? 0.7 : 1,
                      }}
                    >
                      {isSubmitting ? "제출 중..." : "문의하기"}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default InquiryButton;
